import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { canChangeMemberRole, canRemoveMember } from "@/lib/recipe-box-permissions";
import { useRelativeTime } from "@/hooks/use-relative-time";
import { getInitials } from "@/lib/text-utils";
import type { RecipeBox, RecipeBoxMember, RecipeBoxRole } from "@/lib/types";

export type MemberListItemProps = Readonly<{
  member: RecipeBoxMember;
  recipeBox: RecipeBox | null;

  currentUserId?: string | null;
  onRemove: (member: RecipeBoxMember) => void;
  onRoleChange: (member: RecipeBoxMember, newRole: RecipeBoxRole) => void;
  isProcessing: boolean;
  isChangingRole: boolean;
}>;

export const MemberListItem = ({
  member,
  recipeBox,

  currentUserId,
  onRemove,
  onRoleChange,
  isProcessing,
  isChangingRole,
}: MemberListItemProps) => {
  const displayName = member.displayName ?? member.email ?? "Member";
  let secondary: string | null = null;
  if (member.email) {
    const displayNameMatchesEmail = member.displayName ? member.displayName === member.email : false;
    secondary = displayNameMatchesEmail ? null : member.email;
  }
  const joined = useRelativeTime(member.addedAt);
  const canRemove = recipeBox && currentUserId ? canRemoveMember(recipeBox, member, currentUserId) : false;
  const canChangeRole = recipeBox && currentUserId ? canChangeMemberRole(recipeBox, member, currentUserId) : false;

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-background p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        <Avatar className="h-10 w-10 border border-border">
          {member.photoURL ? (
            <AvatarImage src={member.photoURL} alt={displayName} />
          ) : (
            <AvatarFallback className="text-sm font-medium text-muted-foreground">
              {getInitials(displayName)}
            </AvatarFallback>
          )}
        </Avatar>
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium leading-none text-foreground">{displayName}</p>
            <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
              {member.role}
            </Badge>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {secondary ? <span>{secondary}</span> : null}
            {joined ? <span>Joined {joined}</span> : null}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {canChangeRole ? (
          <Select
            value={member.role}
            onValueChange={(value) => {
              if (value !== member.role) {
                onRoleChange(member, value as RecipeBoxRole);
              }
            }}
            disabled={isChangingRole || isProcessing}
          >
            <SelectTrigger className="w-[120px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="viewer">Viewer</SelectItem>
              <SelectItem value="member">Member</SelectItem>
              <SelectItem value="owner">Owner</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
            {member.role}
          </Badge>
        )}
        {canRemove ? (
          <Button variant="ghost" size="sm" onClick={() => onRemove(member)} disabled={isProcessing || isChangingRole}>
            {isProcessing ? "Removing..." : "Remove"}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

